import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import ImageUploader from '@/components/ImageUploader';
import { Country, UpsertCountry } from '@/models/country.model';
import { upsertCountry } from '@/services/country.service';
import { MdFlag } from 'react-icons/md';
import FetchingSelect from '@/components/FetchingSelect';
import { getContinentsLookup } from '@/services/continent.service';

const schema = Schema.Model<UpsertCountry>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  alpha2Code: Schema.Types.StringType().isRequired('This field is required.'),
  alpha3Code: Schema.Types.StringType().isRequired('This field is required.'),
  flagImageUrl: Schema.Types.StringType(),
  continentId: Schema.Types.NumberType(),
});


const emptyModel: UpsertCountry = {
  id: undefined,
  name: undefined,
  alpha2Code: undefined,
  alpha3Code: undefined,
  flagImageUrl: undefined,
  continentId: undefined
};

interface CountryFormProps{
  formObject?: Country;
  pageState: PageState
};

const CountryForm: React.FC<CountryFormProps> = ({formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertCountry>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, });
      return;
    }

    if (formObject) {
      const castedModel: UpsertCountry = { 
        ...formObject, 
      };

      setFormValue(castedModel);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await upsertCountry(formValue);
      if (result.success) {
        nav('/countries');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
      <Form name='upsert-country' fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={schema}>
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, lg:6}}>
              <Form.Group>
                <Form.Label style={{ textAlign: 'center'}}>Flag</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="flagImageUrl"
                  defaultIcon={MdFlag}
                  height="150px"
                  width="150px"
                  url={formValue.flagImageUrl}
                  objectStorageCategory={ObjectStorageFileCategory.Country}
                  onChange={url => setFormValue(pre => ({...pre, flagImageUrl: url}))}              
                />
              </Form.Group>
            </Col>
            <Col span={{xs:24, lg:18}}>
              <Row>
                <Col span={{xs:24, lg:12}}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control name="name" value={formValue.name ?? ''}/>
                  </Form.Group>              
                </Col>
                <Col span={{xs:24, lg:12}}>
                  <Form.Group>
                    <Form.Label>Continent</Form.Label>
                    <Form.Control name="continentId" 
                      accepter={FetchingSelect} block
                      fetchData={(text, page) => getContinentsLookup(text, page)} 
                      placeholder="Select a continent"
                    />
                  </Form.Group>                 
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, lg:12}}>
                  <Form.Group>
                    <Form.Label>Alpha Code (2)</Form.Label>
                    <Form.Control name="localName" value={formValue.alpha2Code ?? ''}/>
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:12}}>
                  <Form.Group>
                    <Form.Label>Alpha Code (2)</Form.Label>
                    <Form.Control name="code" value={formValue.alpha3Code ?? ''}/>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
            {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
          </Row>
        </Grid>
      </Form>
      <Divider />
      
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default CountryForm;

